import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./EditPanel.module.css";
import SearchableDropdown from "../../SearchableDropdown/SearchableDropdown";
import { useLanguage } from "../../../context/LanguageContext";

interface EditPanelProps {
  isOpen: boolean;
  type: "event" | "primitive" | "step";
  data: any;
  onClose: () => void;
  onSave: (data: any) => void;
  onDelete?: () => void;
  modules?: Array<{ id: number; name: string }>;
  eventTypes?: Array<{ id: string | number; name: string }>;
  customSchemas?: Record<string, any>;
  isDeleteModalOpen?: boolean;
}

type ViewMode = "form" | "json";

const TYPE_SCHEMAS: Record<
  string,
  Array<{
    key: string;
    label: string;
    type?: string;
    placeholder?: string;
    focus?: boolean;
  }>
> = {
  primitive: [
    {
      key: "link",
      label: "Variable Name (Link)",
      focus: true,
      placeholder: "e.g., result",
    },
    {
      key: "func",
      label: "Function / Expression",
      placeholder: "e.g., module.action()",
    },
  ],
  if: [
    {
      key: "cond",
      label: "Condition",
      focus: true,
      placeholder: "e.g., x > 10",
    },
  ],
  for: [
    {
      key: "id",
      label: "Iterator Variable",
      focus: true,
      placeholder: "e.g., item",
    },
    { key: "elems", label: "Iterable (List)", placeholder: "e.g., items_list" },
  ],
  break: [],
  function: [
    {
      key: "link",
      label: "Variable Name (Link)",
      focus: true,
      placeholder: "e.g., result",
    },
    { key: "func", label: "Function Name", placeholder: "e.g., my_function" },
    { key: "funcParams", label: "Arguments", placeholder: "e.g., arg1, arg2" },
  ],
  return: [
    {
      key: "func",
      label: "Return Value",
      focus: true,
      placeholder: "e.g., result",
    },
  ],
  throw: [
    {
      key: "funcParams",
      label: "Error Message",
      focus: true,
      placeholder: "e.g., Something went wrong",
    },
  ],
  subtransaction: [
    {
      key: "link",
      label: "Result Variable",
      focus: true,
      placeholder: "e.g., sub_result",
    },
    {
      key: "transactionId",
      label: "Transaction ID",
      type: "number",
      placeholder: "123",
    },
  ],
};

function AutoTextarea({
  value,
  onChange,
  placeholder,
  autoFocus,
  onCtrlEnter,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  onCtrlEnter?: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      className={styles.autoTextarea}
      value={value}
      rows={1}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          onCtrlEnter?.();
        }
      }}
    />
  );
}

export default function EditPanel({
  isOpen,
  type,
  data,
  onClose,
  onSave,
  onDelete,
  isDeleteModalOpen,
  modules = [],
}: EditPanelProps) {
  const [editFormData, setEditFormData] = useState<string>("");
  const [formData, setFormData] = useState<any>({});
  const [viewMode, setViewMode] = useState<ViewMode>("form");

  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen && data) {
      setEditFormData(JSON.stringify(data, null, 2));
      const initialData = { ...data, type: data.type || "primitive" };
      setFormData(initialData);
    }
  }, [isOpen, data]);

  const handleSave = useCallback(() => {
    try {
      let dataToSave: any;

      if (viewMode === "json") {
        dataToSave = JSON.parse(editFormData);
      } else {
        dataToSave = formData;
      }

      if (dataToSave._isNew) {
        delete dataToSave._isNew;
      }

      onSave(dataToSave);
      (document.activeElement as HTMLElement)?.blur();
    } catch (error) {
      alert("Invalid JSON format");
    }
  }, [viewMode, editFormData, formData, onSave]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isDeleteModalOpen) return;

      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA";

      const isArrow = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key);

      if (e.ctrlKey && isArrow) return;
      if (e.altKey && isArrow) return;

      if (e.key === "Enter") {
        if (target.tagName === "TEXTAREA") {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleSave();
          }
          return;
        }
        e.preventDefault();
        handleSave();
      }

      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }

      if (e.key === "Delete") {
        if (isInput) return;
        e.preventDefault();
        if (onDelete) onDelete();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, onDelete, handleSave, isDeleteModalOpen]);

  const handleTypeChange = (newType: string) => {
    const schema = TYPE_SCHEMAS[newType] || [];

    const newData: any = {
      ...formData,
      type: newType,
    };

    schema.forEach((field) => {
      if (newData[field.key] === undefined) {
        newData[field.key] = field.type === "number" ? 0 : "";
      }
    });

    setFormData(newData);
    setEditFormData(JSON.stringify(newData, null, 2));
  };

  const handleFormChange = (key: string, value: any) => {
    const newFormData = { ...formData, [key]: value };
    setFormData(newFormData);
    setEditFormData(JSON.stringify(newFormData, null, 2));
  };

  const handleJsonChange = (value: string) => {
    setEditFormData(value);
    try {
      const parsed = JSON.parse(value);
      setFormData(parsed);
    } catch (error) {}
  };

  const getTitle = () => {
    if (data._isNew) return t("newPrimitive");
    if (formData.type && formData.type !== "primitive") {
      return `${t("edit")} ${formData.type.charAt(0).toUpperCase() + formData.type.slice(1)}`;
    }
    return type === "event" ? t("editEvent") : t("editPrimitive");
  };

  const renderGenericField = (fieldConfig: any) => {
    const { key, label, placeholder, type: inputType, focus } = fieldConfig;
    const value = formData[key];

    if (key === "funcParams" || key === "params") {
      const displayValue = Array.isArray(value)
        ? value.join(", ")
        : value || "";
      return (
        <div key={key} className={styles.formGroup}>
          <label className={styles.fieldLabel}>{label}</label>
          <AutoTextarea
            value={displayValue}
            placeholder={placeholder}
            autoFocus={!!focus}
            onCtrlEnter={handleSave}
            onChange={(val) => {
              const arr = val.split(",").map((s) => s.trim());
              handleFormChange(key, arr);
            }}
          />
        </div>
      );
    }

    if (inputType === "number") {
      return (
        <div key={key} className={styles.formGroup}>
          <label className={styles.fieldLabel}>{label}</label>
          <input
            className={styles.numberInput}
            type="number"
            autoFocus={!!focus}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => handleFormChange(key, parseFloat(e.target.value))}
          />
        </div>
      );
    }
    return (
      <div key={key} className={styles.formGroup}>
        <label className={styles.fieldLabel}>{label}</label>
        <AutoTextarea
          value={value || ""}
          placeholder={placeholder}
          autoFocus={!!focus}
          onCtrlEnter={handleSave}
          onChange={(val) => handleFormChange(key, val)}
        />
      </div>
    );
  };

  const renderContent = () => {
    if (type !== "event") {
      const typeOptions = Object.keys(TYPE_SCHEMAS).map((k) => ({
        label: k.charAt(0).toUpperCase() + k.slice(1),
        value: k,
      }));

      const currentFields =
        TYPE_SCHEMAS[formData.type] || TYPE_SCHEMAS.primitive;

      return (
        <div className={styles.formView}>
          <div className={styles.formGroup}>
            <SearchableDropdown
              label="Action Type"
              options={typeOptions}
              value={formData.type || "primitive"}
              onChange={(val) => handleTypeChange(val)}
            />
          </div>

          {currentFields.map((field) => renderGenericField(field))}

          {currentFields.length === 0 && (
            <div className={styles.infoText}>
              No additional parameters required for <b>{formData.type}</b>.
            </div>
          )}
        </div>
      );
    }

    if (type === "event") {
      const eventOptions = [
        { label: "source_message", value: "source_message" },
        { label: "button_click", value: "button_click" },
        { label: "timer", value: "timer" },
        { label: "webhook", value: "webhook" },
      ];
      return (
        <div className={styles.formView}>
          <div className={styles.formGroup}>
            <SearchableDropdown
              label="Event Type"
              options={eventOptions}
              value={formData.event || ""}
              onChange={(val) => handleFormChange("event", val)}
            />
          </div>
          {Object.keys(formData)
            .filter((k) => k !== "event" && k !== "id")
            .map((key) => (
              <div key={key} className={styles.formGroup}>
                <label className={styles.fieldLabel}>{key}</label>
                <AutoTextarea
                  value={String(formData[key] ?? "")}
                  onCtrlEnter={handleSave}
                  onChange={(val) => handleFormChange(key, val)}
                />
              </div>
            ))}
        </div>
      );
    }

    return (
      <div className={styles.formView}>
        {Object.keys(formData).map((key) => (
          <div key={key} className={styles.formGroup}>
            <label className={styles.fieldLabel}>{key}</label>
            <AutoTextarea
              value={String(formData[key] ?? "")}
              onCtrlEnter={handleSave}
              onChange={(val) => handleFormChange(key, val)}
            />
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h3>{getTitle()}</h3>
        <button className={styles.closeButton} onClick={onClose}>
          ✕
        </button>
      </div>

      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleButton} ${viewMode === "form" ? styles.toggleActive : ""}`}
          onClick={() => setViewMode("form")}
        >
          {t("form")}
        </button>
        <button
          className={`${styles.toggleButton} ${viewMode === "json" ? styles.toggleActive : ""}`}
          onClick={() => setViewMode("json")}
        >
          JSON
        </button>
      </div>

      <div className={styles.panelBody}>
        {viewMode === "form" ? (
          renderContent()
        ) : (
          <div className={styles.formGroup}>
            <label className={styles.fieldLabel}>JSON Data</label>
            <textarea
              value={editFormData}
              onChange={(e) => handleJsonChange(e.target.value)}
              className={styles.jsonTextarea}
            />
          </div>
        )}
      </div>

      <div className={styles.panelFooter}>
        {(type === "primitive" || type === "event") && onDelete && (
          <button className={styles.deleteButton} onClick={onDelete}>
            {t("delete")}
          </button>
        )}
        <button className={styles.cancelButton} onClick={onClose}>
          {t("cancel")}
        </button>
        <button className={styles.saveButton} onClick={handleSave}>
          {t("save")}
        </button>
      </div>
    </div>
  );
}
